import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { SectionWithMeta } from '../drawer-component/components/Sections';
import AddIcon from '@mui/icons-material/Add';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import React, { useState } from 'react';
import { useQuery as useReactQuery, useQueryClient } from 'react-query';
import { useCanCreateNotes, useCanReadNotes } from '@dispatch/shared/hooks';
import { getNotesForTask, TaskNoteDto } from '@dispatch/shared/services';
import { ReviewStatusToggle } from '../review-status/ReviewStatusToggle';
import { AddNoteModal } from './AddNoteModal';
import { RelatedTasksLink } from './related-tasks/RelatedTasksLink';

export interface NotesPanelProps {
  taskId: string;
  tenantId?: string;
  claimNumber?: string;
}

type ModalState =
  | { kind: 'create' }
  | { kind: 'view'; note: TaskNoteDto }
  | null;

const formatTimestamp = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

/**
 * NotesPanel - top-level container for the task-notes section (rendered as
 * its own tab inside TaskDetails). Hosts:
 *   1. `ReviewStatusToggle` for this task (header strip)
 *   2. `RelatedTasksLink` for the parent claim (header strip)
 *   3. `Add note` button → `AddNoteModal` in create mode (gated on
 *      `useCanCreateNotes` / `manage:notes`)
 *   4. `DataGridPro` rendering the note list. Rows are clickable and open
 *      `AddNoteModal` in view mode; users with manage:notes can transition
 *      view → edit inside the modal.
 *
 * Hidden entirely when the current user lacks any notes permission.
 */
export const NotesPanel: React.FC<NotesPanelProps> = ({
  taskId,
  tenantId,
  claimNumber,
}) => {
  const canRead = useCanReadNotes();
  const canCreate = useCanCreateNotes();
  const queryClient = useQueryClient();
  const [modalState, setModalState] = useState<ModalState>(null);
  const [pageSize, setPageSize] = useState<number>(25);

  const queryKey = ['task-notes', taskId];
  const { data, isLoading } = useReactQuery<TaskNoteDto[]>(
    queryKey,
    () => getNotesForTask(taskId),
    { enabled: !!taskId && canRead }
  );

  if (!canRead) return null;

  const notes = data ?? [];
  const onCreated = () => queryClient.invalidateQueries(queryKey);

  const columns: GridColDef[] = [
    {
      field: 'categoryName',
      headerName: 'Category',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<string>) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'body',
      headerName: 'Note',
      flex: 3,
      minWidth: 240,
      renderCell: (params: GridRenderCellParams<string>) => (
        // Single-line ellipsis: long bodies are unreadable when wrapped
        // inside a fixed-height row, so we truncate here and rely on the
        // row click → AddNoteModal (view mode) to show the full body.
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
          }}
          title={params.value ?? ''}
        >
          {params.value ?? ''}
        </Typography>
      ),
    },
    {
      field: 'authoredBy',
      headerName: 'Author',
      flex: 1,
      minWidth: 120,
      // Sort/filter/export must use the same fallback chain the cell
      // displays, or a note shown by account id sorts as blank and
      // collapses to one end of the grid (ADO #104034 AC5).
      valueGetter: ({ row }: GridValueGetterParams<string, TaskNoteDto>) =>
        row.authoredBy || row.authoredByUserId || 'Unknown',
      renderCell: (params: GridRenderCellParams<string>) => (
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'authoredAtUtc',
      headerName: 'Date',
      flex: 1,
      minWidth: 160,
      valueFormatter: ({ value }) => formatTimestamp(value as string),
    },
  ];

  const handleRowClick = (params: GridRowParams<TaskNoteDto>) => {
    setModalState({ kind: 'view', note: params.row });
  };

  return (
    <div style={{ padding: '24px' }} data-testid="notes-panel">
      <SectionWithMeta
        title="Notes"
        meta={() => (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            data-testid="notes-panel-header"
          >
            <ReviewStatusToggle taskId={taskId} tenantId={tenantId} />
            <RelatedTasksLink
              claimNumber={claimNumber}
              excludeTaskId={taskId}
            />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setModalState({ kind: 'create' })}
                data-testid="notes-panel-add-note"
              >
                Add note
              </Button>
            )}
          </Stack>
        )}
      >
        <Box
          sx={{ minHeight: 240, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          data-testid="notes-panel-table-wrap"
        >
          {isLoading ? (
            <Box
              sx={{ paddingY: 6, textAlign: 'center' }}
              data-testid="notes-panel-loading"
            >
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Box data-testid="notes-panel-grid">
              <DataGridPro
                rows={notes}
                getRowId={(row) => row.noteId}
                columns={columns}
                disableSelectionOnClick
                disableColumnMenu
                autoHeight
                density="compact"
                hideFooterSelectedRowCount
                pagination
                pageSize={pageSize}
                onPageSizeChange={(size) => setPageSize(size)}
                rowsPerPageOptions={[10, 25, 50]}
                onRowClick={handleRowClick}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'authoredAtUtc', sort: 'desc' }],
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </SectionWithMeta>
      <AddNoteModal
        taskId={taskId}
        tenantId={tenantId}
        open={modalState !== null}
        mode={modalState?.kind === 'view' ? 'view' : 'create'}
        note={modalState?.kind === 'view' ? modalState.note : undefined}
        onClose={() => setModalState(null)}
        onCreated={onCreated}
      />
    </div>
  );
};
